import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import {
  ProjectIssueFragmentDoc,
  ProjectIssueInput,
  useUpdateProjectIssueMutation,
} from "@/generated/graphql";

const TRACKED_FIELDS = [
  "title",
  "description",
  "status",
  "dueDate",
  "parentId",
  "type",
  "assignees",
] as const satisfies readonly (keyof ProjectIssueInput)[];

export function useUpdateProjectIssue(groupId: string) {
  const [updateProjectIssue] = useUpdateProjectIssueMutation({
    update: (cache, { data }) => {
      const updatedIssues = data?.updateProjectIssue ?? [];
      cache.modify({
        fields: {
          projectIssues() {
            return updatedIssues
              .map((i) => {
                return { ...i, groupId };
              })
              .map((issue) =>
                cache.writeFragment({
                  fragment: ProjectIssueFragmentDoc,
                  fragmentName: "ProjectIssue",
                  data: issue,
                })
              );
          },
        },
      });
    },
  });

  const { track } = useJitsu();

  return useCallback(
    async (issues: ProjectIssueInput[]) => {
      const result = await updateProjectIssue({
        variables: { input: { groupId, issues } },
      });

      // `fields_sent`, not `fields_changed`: the form path submits every field
      // while the dropdowns send one, so this is what the input carried.
      for (const issue of issues) {
        // Presence, not nullness: clearing a due date sends `null`, and that is
        // exactly the change the event should report.
        const fields = TRACKED_FIELDS.filter(
          (field) => issue[field] !== undefined
        );

        if (issue.deleted === true) {
          track(JitsuEvent.GROUP_TASK_DELETED, {
            group_id: groupId,
            issue_id: issue.id,
          });
        } else if (issue.id == null) {
          // No issue_id: the mutation returns the whole board, not the new row.
          track(JitsuEvent.GROUP_TASK_CREATED, {
            group_id: groupId,
            issue_type: issue.type,
            has_assignee: (issue.assignees?.length ?? 0) > 0,
            has_due_date: issue.dueDate != null,
          });
        } else if (fields.length > 0) {
          track(JitsuEvent.GROUP_TASK_UPDATED, {
            group_id: groupId,
            issue_id: issue.id,
            fields_sent: fields,
          });
        }
      }

      return result;
    },
    [updateProjectIssue, groupId, track]
  );
}
